import { useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { ActionText } from '@components/action-text';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconIntegration } from '@components/icons/integration';
import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconTrash } from '@components/icons/trash';
import { ListCard } from '@components/list-card';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';

import { useDeleteIntegration, useIntegrationList } from 'api/integrations/hooks';
import { Integration } from 'api/integrations/requests';
import { routePaths } from 'ui/authenticated/navigation';

import { useDuplicateIntegrationModal } from './modals/duplicate-integration';
import { useIntegrationAddEditSidebar } from './sidebars/add-edit-integration';
import { AuthenticationStatus } from './sidebars/authentication';

export const renderIntegrationIcon = (integration: Integration) => {
  return integration.thumbnail ? <SquareImage src={integration.thumbnail} /> : <IconIntegration />;
};

const orderingItems = [
  'name',
  '-name',
  '-date_created',
  'date_created',
  '-date_updated',
  'date_updated',
] as const;

export const Integrations = () => {
  const [searchStr, setSearchStr] = useState('');
  const [ordering, setOrdering] = useLocalStorageState<(typeof orderingItems)[number]>({
    key: 'integration-list-order',
    defaultValue: '-date_created',
  });
  const queryString = useMemo(() => {
    return toQueryString({
      ...(searchStr && { search: searchStr }),
      ordering,
    });
  }, [searchStr, ordering]);

  const integrationsQuery = useIntegrationList({ queryString });
  const showAddEditSidebar = useIntegrationAddEditSidebar();
  const showDeleteModal = useDeleteModal();
  const showDuplicateModal = useDuplicateIntegrationModal();
  const deleteMutation = useDeleteIntegration();
  const { t, styledTranslate } = useScopedTranslate(locale);
  if (!integrationsQuery.data) {
    return <Spinner />;
  }

  const integrations = integrationsQuery.data;
  const isDataEmpty = !integrations || integrations.length === 0;
  const isDescendingOrdering = ordering.startsWith('-');

  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: t('customIntegrations') }]} />}
        actions={
          <Stack space={2} orientation="horizontal">
            <ExpandableSearch onChange={setSearchStr} />
            <Menu
              maxWidth="auto"
              width="auto"
              buttonProps={{
                variant: 'secondary',
                text: t(`sort_${ordering}`).split(' • ')[0],
                iconLeft: isDescendingOrdering ? <IconSortDesc /> : <IconSortAsc />,
              }}
            >
              <Menu.Label label={t('sort')} />
              {orderingItems.map((it) => (
                <Menu.Item
                  key={it}
                  onSelect={() => setOrdering(it)}
                  isActive={ordering === it}
                  label={t(`sort_${it}`)}
                />
              ))}
            </Menu>
            <Button onClick={() => showAddEditSidebar()} text={t('addIntegration')} />
          </Stack>
        }
      />
      <PageContent>
        {isDataEmpty ? (
          searchStr ? (
            <EmptySearchState skipMargin />
          ) : (
            <EmptyState
              skipMargin
              icon={<IconIntegration size={80} />}
              header={t('noIntegrationsTitle')}
              content={styledTranslate('noIntegrationsDescription', {
                action: (
                  <ActionText.ExternalLink
                    href="https://help.journeys.50skills.app/en/"
                    color="textSecondary"
                    underline
                  >
                    {t('aboutIntegrations')}
                  </ActionText.ExternalLink>
                ),
              })}
            />
          )
        ) : (
          <Stack space={2}>
            {integrationsQuery.data.map((integration) => (
              <ListCard
                key={integration.shortId}
                to={generatePath(routePaths.integrationDetail, {
                  integrationId: integration.shortId,
                  '*': null,
                })}
                spaceX={6}
                spaceY={4}
                title={integration.name}
                description={integration.description}
                details={<AuthenticationStatus integration={integration} />}
                icon={renderIntegrationIcon(integration)}
                actionItems={[
                  {
                    icon: <IconEdit />,
                    label: t('core.edit'),
                    onSelect: () => showAddEditSidebar({ integration }),
                  },
                  {
                    icon: <IconDuplicate />,
                    label: t('duplicate'),
                    onSelect: () => showDuplicateModal({ integration }),
                  },
                  'separator',
                  {
                    icon: <IconTrash />,
                    label: t('deleteIntegration'),
                    variant: 'danger',
                    onSelect: () =>
                      showDeleteModal({
                        onConfirm: () => deleteMutation.mutateAsync(integration.shortId),
                        title: t('deleteIntegration'),
                        hasUserConfirmation: true,
                        description: t('deleteDescription', { itemTitle: integration.name }),
                      }),
                  },
                ]}
                actions={
                  <ValueList
                    items={[
                      { value: integration.numJourneys, label: t('numberOfJourneys') },
                      {
                        value: integration.endpoints.length + integration.webhooks.length,
                        label: t('numberOfEndpoints'),
                      },
                    ]}
                  />
                }
              />
            ))}
          </Stack>
        )}
      </PageContent>
    </>
  );
};

const locale = {
  en: {
    addIntegration: 'Add new integration',
    deleteIntegration: 'Delete integration',
    deleteDescription:
      'Are you sure you want to delete {itemTitle}?\nDeleting this integration might impact your existing workflows and synced lists. Are you sure you want to continue?',
    customIntegrations: 'Custom integrations',
    noIntegrationsTitle: 'No custom integrations',
    noIntegrationsDescription:
      'Create a custom connector to connect to a REST API.\nThe API must support one of 6 authentication methods and use JSON.\n{action}',
    aboutIntegrations: 'Learn more about integrations',
    numberOfJourneys: '# Journeys',
    numberOfEndpoints: '# Endpoints',
    sort: 'Sort by',
    sort_name: 'Alphabetical ascending • A → Z',
    'sort_-name': 'Alphabetical descending • Z → A',
    'sort_-date_created': 'Created • Newest',
    sort_date_created: 'Created • Oldest',
    'sort_-date_updated': 'Last edit • New → Old',
    sort_date_updated: 'Last edit • Old → New',
    duplicate: 'Duplicate',
  },
};
